import { Minus, Plus } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface Props {
  value: number;
  onChange: (value: number) => void;
  min?: number | undefined;
  className?: string | undefined;
  label?: string | undefined;
  size?: "default" | "compact" | undefined;
}

export function QuantityStepper({
  value,
  onChange,
  min = 1,
  className,
  label,
  size = "default",
}: Props) {
  const { t } = useI18n();
  const compact = size === "compact";

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full border border-border bg-card",
        compact ? "gap-0.5 p-0.5" : "gap-1 p-1",
        className,
      )}
      role="group"
      aria-label={label ?? t("common.quantity")}
    >
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= min}
        aria-label={t("common.decreaseQuantity")}
        className={cn(
          "pressable grid place-items-center rounded-full text-foreground hover:bg-accent disabled:opacity-40",
          compact ? "size-9" : "size-11",
        )}
      >
        <Minus className={compact ? "size-3.5" : "size-4"} aria-hidden="true" />
      </button>
      <span
        className={cn(
          "text-center font-bold tabular-nums",
          compact ? "min-w-6 text-xs" : "min-w-8 text-sm",
        )}
        aria-live="polite"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        aria-label={t("common.increaseQuantity")}
        className={cn(
          "pressable grid place-items-center rounded-full bg-primary text-primary-foreground hover:brightness-110",
          compact ? "size-9" : "size-11",
        )}
      >
        <Plus className={compact ? "size-3.5" : "size-4"} aria-hidden="true" />
      </button>
    </div>
  );
}
